import { useState, useEffect, useCallback, useRef } from 'react';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import TabBar from '../components/ui/TabBar';
import DataTable, { Column } from '../components/ui/DataTable';
import ActionButton from '../components/ui/ActionButton';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';

const API = `${API_V1}/treasury`;

interface UnmatchedItem { id: number; transaction_date: string; description: string; amount: number; external_reference: string; }
interface ReconSummary { total_lines: number; matched: number; unmatched: number; match_rate: number; unmatched_amount: number; }
interface MonthEnd { period: string; bank_balance: number; gl_balance: number; difference: number; reconciled: boolean; }

export default function BankReconciliation() {
  const { entity } = useGlobalEntity();
  const [summary, setSummary] = useState<ReconSummary | null>(null);
  const [unmatched, setUnmatched] = useState<UnmatchedItem[]>([]);
  const [monthEnd, setMonthEnd] = useState<MonthEnd | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('summary');
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [meYear, setMeYear] = useState('2026');
  const [meMonth, setMeMonth] = useState('4');

  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, unmRes] = await Promise.all([
        authFetch(withEntityParam(`${API}/reconciliation/summary`, entity)),
        authFetch(withEntityParam(`${API}/reconciliation/unmatched?limit=50`, entity)),
      ]);
      if (sumRes.ok) setSummary(await sumRes.json());
      if (unmRes.ok) { const d = await unmRes.json(); setUnmatched(d.items || []); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [entity]);

  useEffect(() => { load(); }, [load]);

  const handleAutoRecon = async () => {
    const res = await authFetch(`${API}/reconciliation/auto`, { method: 'POST' });
    if (res.ok) { const d = await res.json(); showToast(`Auto-reconciled: ${d.exact_matches || 0} exact + ${d.fuzzy_matches || 0} fuzzy`, true); load(); }
    else showToast('Failed', false);
  };

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const handleUploadOfx = () => fileInputRef.current?.click();
  const handleOfxFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = '';
    const fd = new FormData();
    fd.append('file', f);
    const url = entity ? `${API}/bank-statements/upload-ofx?entity_code=${encodeURIComponent(entity)}` : `${API}/bank-statements/upload-ofx`;
    try {
      const res = await authFetch(url, { method: 'POST', body: fd });
      if (res.ok) {
        const d = await res.json();
        showToast(`OFX ingested: ${d.lines_created} lines (${d.lines_skipped_duplicate} dupes)`, true);
        load();
      } else {
        const err = await res.json();
        showToast(err.detail || 'OFX upload failed', false);
      }
    } catch { showToast('OFX upload failed', false); }
  };

  const handleCategorize = async (bankLineId: number, category: string) => {
    try {
      const res = await authFetch(`${API}/reconciliation/${bankLineId}/categorize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category }),
      });
      if (res.ok) {
        const d = await res.json();
        showToast(`Categorized as ${category}: JE ${d.je_number} posted`, true);
        load();
      } else {
        const e = await res.json();
        showToast(e.detail || 'Categorize failed', false);
      }
    } catch { showToast('Categorize failed', false); }
  };

  const categoryButtonStyle: React.CSSProperties = {
    padding: '3px 8px', fontSize: 11, borderRadius: 4, border: '1px solid var(--border-subtle)',
    background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer', marginRight: 4,
  };

  const loadMonthEnd = async () => {
    const res = await authFetch(`${API}/reconciliation/month-end?year=${meYear}&month=${meMonth}`);
    if (res.ok) setMonthEnd(await res.json());
  };

  const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12, background: 'var(--bg-card)', color: 'var(--text-primary)' };

  const unmatchedColumns: Column[] = [
    { key: 'transaction_date', label: 'Date' },
    { key: 'description', label: 'Description', render: (v: string) => v || '-' },
    { key: 'amount', label: 'Amount', align: 'right', render: (v: number) => <span style={{ fontWeight: 600 }}>{formatAmount(v)}</span> },
    { key: 'external_reference', label: 'Reference', render: (v: string) => <span style={{ color: 'var(--text-secondary)' }}>{v || '-'}</span> },
    {
      key: 'id', label: 'Categorize', render: (_v: any, row: UnmatchedItem) => {
        const isInflow = (row.amount || 0) > 0;
        return (
          <div>
            {isInflow ? (
              <>
                <button type="button" style={categoryButtonStyle} title="DR Cash / CR Interest Income"
                        onClick={() => handleCategorize(row.id, 'INTEREST_INCOME')}>Interest</button>
                <button type="button" style={categoryButtonStyle} title="DR Cash / CR FX Gain"
                        onClick={() => handleCategorize(row.id, 'FX_GAIN')}>FX Gain</button>
              </>
            ) : (
              <>
                <button type="button" style={categoryButtonStyle} title="DR Bank Fees / CR Cash"
                        onClick={() => handleCategorize(row.id, 'BANK_FEE')}>Bank Fee</button>
                <button type="button" style={categoryButtonStyle} title="DR FX Loss / CR Cash"
                        onClick={() => handleCategorize(row.id, 'FX_LOSS')}>FX Loss</button>
              </>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <PageLayout
      title="Bank Reconciliation"
      subtitle="Match bank lines to GL entries, formal month-end reconciliation"
      actions={[
        { label: 'Upload OFX', onClick: handleUploadOfx, variant: 'secondary' },
        { label: 'Auto-Reconcile', onClick: handleAutoRecon },
      ]}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".ofx,.qfx,application/x-ofx,application/vnd.intu.qfx"
        onChange={handleOfxFile}
        title="OFX / QFX bank statement file picker"
        aria-label="Upload OFX bank statement"
        style={{ display: 'none' }}
      />
      {summary && (
        <KpiGrid
          columns={5}
          items={[
            { label: 'Total Lines', value: summary.total_lines },
            { label: 'Matched', value: summary.matched, color: 'var(--positive)' },
            { label: 'Unmatched', value: summary.unmatched, color: 'var(--negative)' },
            { label: 'Match Rate', value: `${summary.match_rate}%`, color: 'var(--accent)' },
            { label: 'Unmatched Amt', value: formatAmountCompact(summary.unmatched_amount), color: 'var(--negative)' },
          ]}
        />
      )}

      <TabBar
        tabs={[
          { key: 'summary', label: 'Summary' },
          { key: 'unmatched', label: 'Unmatched Items' },
          { key: 'monthend', label: 'Month-End' },
        ]}
        active={tab}
        onChange={(t) => { setTab(t); if (t === 'monthend') loadMonthEnd(); }}
      />

      {tab === 'summary' ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>Select a tab above to view reconciliation details.</div>
      ) : tab === 'unmatched' ? (
        <DataTable
          columns={unmatchedColumns}
          rows={unmatched}
          loading={loading}
          emptyMessage="All bank lines matched."
          rowKey="id"
        />
      ) : (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <input value={meYear} onChange={e => setMeYear(e.target.value)} style={{ ...inputStyle, width: 80 }} placeholder="Year" />
            <input value={meMonth} onChange={e => setMeMonth(e.target.value)} style={{ ...inputStyle, width: 60 }} placeholder="Month" />
            <ActionButton label="Load" onClick={loadMonthEnd} />
          </div>
          {monthEnd && (
            <KpiGrid
              columns={4}
              items={[
                { label: 'Bank Balance', value: formatAmountCompact(monthEnd.bank_balance) },
                { label: 'GL Balance', value: formatAmountCompact(monthEnd.gl_balance) },
                { label: 'Difference', value: formatAmountCompact(monthEnd.difference), color: monthEnd.reconciled ? 'var(--positive)' : 'var(--negative)' },
                { label: 'Status', value: monthEnd.reconciled ? 'RECONCILED' : 'UNRECONCILED', color: monthEnd.reconciled ? 'var(--positive)' : 'var(--negative)' },
              ]}
            />
          )}
        </div>
      )}

      {toast && <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 2000, padding: '12px 20px', borderRadius: 8, background: toast.ok ? 'var(--positive-bg)' : 'var(--negative-bg)', color: toast.ok ? 'var(--positive)' : 'var(--negative)', fontSize: 13, fontWeight: 600 }}>{toast.msg}</div>}
    </PageLayout>
  );
}
